import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import {
  ToggleGroup,
  ToggleGroupItem
} from '@/shared/ui/toggle-group';
import type { OrganizationOverviewData } from '@/shared/types/overviewTypes';
import {
  Bar,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  ReferenceLine,
  LabelList,
  XAxis,
  ResponsiveContainer,
} from 'recharts';
import {
  BAR_MARGIN_TIME_SERIES,
  BAR_TOP_RADIUS,
  CHART_OPACITY,
  REVERSE_MONTH_KEYS,
  MonthRangeOption,
  calendarPrevYm,
  TICK_FONT_SIZE,
  LABEL_FONT_SIZE,
  compareMonthKeysAsc,
} from '@/domains/global-dashboard/utils/chart-utils';
import { formatNumber } from '@/shared/utils/format-utils';
import {
  DashboardBarChart,
  DashboardBarGrid,
  DashboardBarPlotHoverCursor,
} from '@/domains/global-dashboard/components/GlobalDashboardBarChart';
import { barTopNumericLabelProps } from '@/domains/global-dashboard/components/dashboardChart';
import { useMemo, useState } from 'react';
import { fleetSnapshotByYm, type FleetMonthSnap } from '@/domains/global-dashboard/sections/service-lines/fleet-utils';

/** Value labels above positive bars / below negative bars (Recharts SVG coords; avoids overlap with zero line & months). */
function fleetNetNewEvolutionBarLabel(props: {
  x?: unknown;
  y?: unknown;
  width?: unknown;
  height?: unknown;
  value?: unknown;
}) {
  const x = Number(props.x);
  const y = Number(props.y);
  const width = Number(props.width);
  const rawH = props.height;
  const h = rawH === undefined ? Number.NaN : Number(rawH);
  const vRaw = props.value;
  const v =
    typeof vRaw === 'number'
      ? vRaw
      : typeof vRaw === 'string'
        ? Number.parseFloat(vRaw)
        : Number.NaN;

  if (!Number.isFinite(x) || !Number.isFinite(width) || !Number.isFinite(y) || !Number.isFinite(v) || v === 0) {
    return null;
  }

  const sh = Number.isFinite(h) ? h : 0;
  const topEdge = Math.min(y, y + sh);
  const botEdge = Math.max(y, y + sh);

  /** Pull texts away from baseline & month ticks: negatives read downward. */
  const textY = v < 0 ? botEdge + 14 : topEdge - 10;

  return (
    <text x={x + width / 2} y={textY} fill="hsl(var(--foreground))" fontSize={11} fontWeight={600} textAnchor="middle">
      {formatNumber(v)}
    </text>
  );
}

/** Get the fleet metric from two fleet month snapshots for a given mode. */
function fleetMetricFromSnaps(
  cur: FleetMonthSnap | undefined,
  prev: FleetMonthSnap | undefined,
  mode: 'total' | 'active' | 'netNew',
): number {
  if (mode === 'total') return cur?.total ?? 0;
  if (mode === 'active') return cur?.active ?? 0;
  if (!prev) return 0;
  return (cur?.total ?? 0) - prev.total;
}

/** One Recharts row per month — metric per market (`organizationId`), aligned with evolution chart `dataKey`s. */
type FleetEvolutionChartRow = Record<string, string | number> & { month: string };

/** Build a fleet evolution chart row for a given month. */
function buildFleetEvolutionRowForMonth(
  ym: string,
  organizationSeries: { organization: OrganizationOverviewData; months: Map<string, FleetMonthSnap> }[],
  mode: 'total' | 'active' | 'netNew',
): FleetEvolutionChartRow {
  const prevYm = calendarPrevYm(ym);
  const point: FleetEvolutionChartRow = { month: ym };
  for (const { organization, months } of organizationSeries) {
    const cur = months.get(ym);
    const prev = months.get(prevYm);
    point[organization.organizationId] = fleetMetricFromSnaps(cur, prev, mode);
  }
  return point;
}

/** Main component for the fleet overview section. */
export function FleetEvolutionChart({
  selectedOrganizations,
  accountChartColorById,
}: {
  selectedOrganizations: OrganizationOverviewData[];
  accountChartColorById?: Map<string, string>;
}) {
  const [temporalMetric, setTemporalMetric] = useState<'total' | 'active' | 'netNew'>('total');
  const [monthRange, setMonthRange] = useState<MonthRangeOption>(12);

  const organizationSeries = useMemo(() => {
    return selectedOrganizations.map((organization) => ({
      organization,
      months: fleetSnapshotByYm(organization),
    }));
  }, [selectedOrganizations]);

  const chartMonthTimeline = useMemo(() => {
    const keys = new Set<string>();
    for (const { months } of organizationSeries) {
      for (const ym of months.keys()) keys.add(ym);
    }
    return keys.size > 0
      ? [...keys].sort(compareMonthKeysAsc)
      : [...REVERSE_MONTH_KEYS];
  }, [organizationSeries]);

  const monthWindow = useMemo(() => {
    return chartMonthTimeline.slice(-monthRange);
  }, [monthRange, chartMonthTimeline]);

  const overallFleetEvolutionData = useMemo(() => {
    const mode = temporalMetric === 'total' ? 'total' : temporalMetric === 'active' ? 'active' : 'netNew';
    return chartMonthTimeline
      .map((ym) => buildFleetEvolutionRowForMonth(ym, organizationSeries, mode))
      .sort((a, b) => compareMonthKeysAsc(String(a.month), String(b.month)));
  }, [chartMonthTimeline, organizationSeries, temporalMetric]);

  const selectedRangeFleetEvolutionData = useMemo(() => {
    const want = new Set(monthWindow);
    return overallFleetEvolutionData.filter((row) => want.has(String(row.month)));
  }, [overallFleetEvolutionData, monthWindow]);

  /** Net-new MoM can go negative; draw a y=0 baseline only when the series dips below zero. */
  const fleetEvolutionNetNewHasNegative = useMemo(() => {
    if (temporalMetric !== 'netNew') return false;
    for (const row of selectedRangeFleetEvolutionData) {
      for (const o of selectedOrganizations) {
        const raw = row[o.organizationId];
        const v = typeof raw === 'number' ? raw : Number(raw);
        if (!Number.isNaN(v) && v < 0) return true;
      }
    }
    return false;
  }, [temporalMetric, selectedRangeFleetEvolutionData, selectedOrganizations]);

  return (
    <>
      {/* Service lines evolution */}
      < Card >
        <CardHeader className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4 justify-end items-center w-full">
            <div className="col-span-1">
              <CardTitle className="text-base">Service Lines' evolution</CardTitle>
              <CardDescription>Switch between cumulative service lines and net new per month for the selected time range. Suspended service lines are only eliminated the month after their suspension.</CardDescription>
            </div>
            <div className="col-span-1 flex items-center justify-end">
              <ToggleGroup
                type="single"
                value={temporalMetric}
                onValueChange={(v) => v && setTemporalMetric(v as 'total' | 'active' | 'netNew')}
                size="sm"
                variant="outline"
              >
                <ToggleGroupItem value="total">All Service Lines</ToggleGroupItem>
                <ToggleGroupItem value="netNew">Net new / month</ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>
          <div className="flex flex-col gap-2 justify-end">
            <ToggleGroup
              type="single"
              value={monthRange.toString()}
              onValueChange={(v) => v && setMonthRange(Number(v) as MonthRangeOption)}
              size="default"
              variant="outline"
            >
              <ToggleGroupItem value="1">1-Month</ToggleGroupItem>
              <ToggleGroupItem value="3">3-Months</ToggleGroupItem>
              <ToggleGroupItem value="6">6-Months</ToggleGroupItem>
              <ToggleGroupItem value="12">12-Months</ToggleGroupItem>
            </ToggleGroup>
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="h-[500px]">
            <ResponsiveContainer width="100%" height="100%">
              <DashboardBarChart
                data={selectedRangeFleetEvolutionData}
                variant="timeSeries"
                margin={
                  temporalMetric === 'netNew'
                    ? {
                      ...BAR_MARGIN_TIME_SERIES,
                      bottom: fleetEvolutionNetNewHasNegative ? 88 : BAR_MARGIN_TIME_SERIES.bottom + 12,
                    }
                    : undefined
                }
              >
                <DashboardBarGrid />
                {fleetEvolutionNetNewHasNegative ? (
                  <ReferenceLine y={0} stroke="hsl(var(--border))" strokeWidth={1} />
                ) : null}
                <XAxis dataKey="month" tick={{ fontSize: TICK_FONT_SIZE }} textAnchor="middle" tickMargin={6} />
                <YAxis
                  tick={{ fontSize: TICK_FONT_SIZE }}
                  allowDecimals={false}
                  domain={
                    temporalMetric === 'netNew' && fleetEvolutionNetNewHasNegative
                      ? [
                        (min: number) => {
                          const m = Math.min(0, min);
                          if (!Number.isFinite(m) || m >= 0) return Math.min(0, min);
                          const pad = Math.max(Math.abs(m) * 0.08, Math.abs(min) > 1 ? 1 : 0.35);
                          return m - pad;
                        },
                        (max: number) => Math.max(0, max),
                      ]
                      : undefined
                  }
                  label={{ value: 'Number of Service Lines', angle: -90, position: 'middle', fontSize: LABEL_FONT_SIZE }}
                />
                <RechartsTooltip cursor={<DashboardBarPlotHoverCursor />} />
                <Legend />
                {selectedOrganizations.map((o) => {
                  const c = accountChartColorById?.get(o.organizationId) ?? 'hsl(var(--primary))';
                  return (
                    <Bar
                      key={o.organizationId}
                      dataKey={o.organizationId}
                      name={o.name}
                      fill={c}
                      fillOpacity={CHART_OPACITY.marketBar}
                      radius={BAR_TOP_RADIUS}
                    >
                      {temporalMetric === 'netNew' ? (
                        <LabelList content={fleetNetNewEvolutionBarLabel} />
                      ) : (
                        <LabelList
                          {...barTopNumericLabelProps}
                          formatter={(value: unknown) => formatNumber(typeof value === 'number' ? value : Number(value))}
                        />
                      )}
                    </Bar>
                  );
                })}
              </DashboardBarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card >
    </>
  );
}
